import { useEffect, useState } from 'react';
import { useStressTestStore } from '../../store/stressTestStore';
import { parseUtcTimestamp } from '../../utils/timeUtils';
import { fmt } from '../../utils/numberFormat';
import { collapseSharePct } from '../../utils/planningLabels';

const formatTs = (iso?: string) =>
  iso ? parseUtcTimestamp(iso).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' }) : '–';

/** Persisted planning runs of the zone — reopen or delete. */
export function RunHistoryCard() {
  const stressZoneId = useStressTestStore(s => s.stressZoneId);
  const runHistory = useStressTestStore(s => s.runHistory);
  const isHistoryLoading = useStressTestStore(s => s.isHistoryLoading);
  const loadRunHistory = useStressTestStore(s => s.loadRunHistory);
  const openRun = useStressTestStore(s => s.openRun);
  const openRolloutRun = useStressTestStore(s => s.openRolloutRun);
  const deleteRun = useStressTestStore(s => s.deleteRun);
  const currentRunId = useStressTestStore(s => s.run?.runId ?? s.rollout?.runId);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (stressZoneId) {
      void loadRunHistory(stressZoneId);
    }
  }, [stressZoneId, loadRunHistory]);

  if (!stressZoneId) return null;

  return (
    <div className="rounded-lg border border-gray-200 dark:border-primary-600 bg-white dark:bg-primary-700 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="w-full px-3 py-2.5 flex items-center justify-between text-left"
      >
        <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-800 dark:text-primary-100">
          Frühere Läufe {runHistory.length > 0 && <span className="text-gray-400 dark:text-primary-400 font-normal">({runHistory.length})</span>}
        </h3>
        <svg className={`w-4 h-4 text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="px-3 pb-2.5">
          {isHistoryLoading && runHistory.length === 0 ? (
            <p className="text-xs text-gray-500 dark:text-primary-400">Lade…</p>
          ) : runHistory.length === 0 ? (
            <p className="text-xs text-gray-500 dark:text-primary-400">Noch keine gespeicherten Läufe in dieser Zone.</p>
          ) : (
            <ul className="divide-y divide-gray-100 dark:divide-primary-600">
              {runHistory.map(r => (
                <li key={r.id} className={`py-1.5 flex items-center gap-2 ${r.id === currentRunId ? 'bg-accent-primary/5 dark:bg-accent-primary/10 rounded px-1' : 'px-1'}`}>
                  <button type="button" onClick={() => void (r.kind === 'rollout' ? openRolloutRun(r.id) : openRun(r.id))} className="flex-1 min-w-0 text-left">
                    <span className="block text-xs text-gray-800 dark:text-primary-100 truncate">
                      {r.kind === 'rollout' && <span className="text-[10px] px-1 mr-1 rounded bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300">Hochlauf</span>}
                      {r.label || r.scenarioName || 'Ohne Szenario'}
                      <span className="text-gray-400 dark:text-primary-400"> · {formatTs(r.createdAt)}</span>
                    </span>
                    <span className="block text-[10px] text-gray-500 dark:text-primary-400 truncate">
                      {r.mode === 'archive' ? (r.direction === 'feedIn' ? 'Einspeise-Frame' : 'Starklast-Frame') + (r.frameAt ? ` ${formatTs(r.frameAt)}` : '') : 'synthetisch'}
                      {r.collapseCarriedFraction != null ? (
                        <span className="text-red-600 dark:text-red-400">
                          {' · '}Spannungskollaps{r.collapseHasLimitFrame === false ? ' ohne Zubau' : `, trägt ${collapseSharePct({ carriedFraction: r.collapseCarriedFraction })} %`}
                        </span>
                      ) : (
                        <>
                          {' · '}{fmt(r.maxLoadingWithoutPct, 0)} → {fmt(r.maxLoadingWithPct, 0)} %{r.kind === 'rollout' ? ' (P50, letztes Stützjahr)' : ''}
                          {r.newOverloads > 0 && <span className="text-red-600 dark:text-red-400"> · {r.newOverloads} {r.kind === 'rollout' ? 'erwartete Grenzwertbrüche' : 'neu überlastet'}</span>}
                        </>
                      )}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => void deleteRun(r.id)}
                    title="Lauf löschen"
                    className="shrink-0 p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
